import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { getAllItemRequestsForAdmin } from '@/redux/states/admin/orders/getAllItemRequestsForAdmin/getAllItemRequestsForAdmin.slice'
import { getOfferRequestPaymentDetailsForAdmin } from '@/redux/states/admin/orders/getOfferRequestPaymentDetailsForAdmin/getOfferRequestPaymentDetailsForAdmin.slice'
import { getOfferRequestTripDetailsForAdmin } from '@/redux/states/admin/orders/getOfferRequestTripDetailsForAdmin/getOfferRequestTripDetailsForAdmin.slice'
import { getTripDetailsForAdmin } from '@/redux/states/admin/trips/getTripDetailsForAdmin/getTripDetailsForAdmin.slice'
import { getTripOfferRequestsForAdmin } from '@/redux/states/admin/trips/getTripOfferRequestsForAdmin/getTripOfferRequestsForAdmin.slice'
import { getTripPaymentDetailsForAdmin } from '@/redux/states/admin/trips/getTripPaymentDetailsForAdmin/getTripPaymentDetailsForAdmin.slice'
import { User } from '@/shared/types/users.types'
import { Box } from '@mui/material'
import { useSelector } from 'react-redux'

import EmptyState from '../../../../shared/components/EmptyState/EmptyState.component'
import Table from '../../../../shared/components/Table/Table.component'
import Tabs from '../../../../shared/components/Tabs/Tabs.component'

import env from '../../../../networkRequests/apiClient/env.config'
import { getLockerOrders } from '../../../../redux/states/admin/lockers/getLockerOrders/getLockerOrders.slice'
import {
  GetLockerOrdersRequestPayload,
  LockerOrderListItem,
  LockerOrderParty
} from '../../../../redux/states/admin/lockers/getLockerOrders/getLockerOrders.types'
import { RootState } from '../../../../redux/store/store.types'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import { adminNavigationPaths } from '../../AdminDashboard.dictionary'
import { getLockerOrderTableColumns, lockerOrderStatusTabs } from './LockerOrders.dictionary'
import styles from './LockerOrders.styles'
import { LockerOrdersProps } from './LockerOrders.types'

/** The header of the order and trip pages names the person whose page it is. */
const toUser = (party: LockerOrderParty): User => ({
  id: Number(party.hurrierId),
  firstName: party.firstName,
  lastName: party.lastName,
  email: party.email
})

/**
 * Past locker orders, for support.
 *
 * The Lockers screen answers "what is in the lockers right now" — it reads live
 * bank state and keeps nothing. When a shopper writes in about a collection that
 * already happened there was nowhere to look it up, even though every locker
 * order is recorded. This lists them newest-first, a tab per status, and links
 * each one to its Hurrier order, the item in it and the trip that carried it, so
 * a support conversation that starts from any of those can reach the others.
 */
const LockerOrders: React.FC<LockerOrdersProps> = (props) => {
  const { searchQuery, demoRouter, setSelectedUser } = props

  const [page, setPage] = useState(1)
  const [statusTabIndex, setStatusTabIndex] = useState(0)
  const pageSize = env.DEFAULT_PAGE_SIZE
  const dispatch = useAppDispatch()

  const { loading, success } = useSelector((state: RootState) => state.getLockerOrders)

  const statusTab = lockerOrderStatusTabs[statusTabIndex]

  const fetchOrders = useCallback(
    (nextPage: number, status?: string) => {
      const payload: GetLockerOrdersRequestPayload = {
        request: {
          limit: pageSize,
          offset: (nextPage - 1) * pageSize,
          ...(status ? { status } : {})
        }
      }

      dispatch(getLockerOrders(payload))
    },
    [dispatch, pageSize]
  )

  useEffect(() => {
    fetchOrders(page, statusTab.status)
  }, [fetchOrders, page, statusTab.status])

  const handleStatusTabChange = (tabIndex: number) => {
    setStatusTabIndex(tabIndex)
    setPage(1)
  }

  const orders = useMemo(() => success?.data.orders ?? [], [success])

  // The endpoint filters by status, bank and date, but not by free text, so the
  // header's search box is applied here over what is on the page rather than
  // pretending to search the whole history.
  const visibleOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return orders

    return orders.filter((order) =>
      [
        order.orderNumber ? `#${order.orderNumber}` : '',
        order.externalOrderId,
        order.item ? `#${order.item.itemRequestId}` : '',
        order.trip ? `${order.trip.id} ${order.trip.origin} ${order.trip.destination}` : '',
        `${order.shopper.firstName} ${order.shopper.lastName}`,
        `${order.traveler.firstName} ${order.traveler.lastName}`,
        order.lockerBank.name,
        order.lockerBank.cityName
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [orders, searchQuery])

  const total = success?.data.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Open the Hurrier order this locker order belongs to, through the same route and the same
  // requests as the Offer Requests list, so support lands on the familiar page with it loaded.
  // Opening it for an item adds that item to the route, and the page brings its card into view.
  const openOrder = useCallback(
    (order: LockerOrderListItem, focusedItemRequestId?: number) => {
      if (!order.orderNumber || !order.shopper.hurrierId) return

      const offerRequestId = order.orderNumber
      const orderPath = `/${adminNavigationPaths.offerRequests}/${order.shopper.hurrierId}/Orders/${offerRequestId}`

      setSelectedUser(toUser(order.shopper))
      demoRouter.navigate(focusedItemRequestId ? `${orderPath}/Items/${focusedItemRequestId}` : orderPath)
      dispatch(getAllItemRequestsForAdmin({ request: { page: 1, size: pageSize, offerRequestId } }))
      dispatch(getOfferRequestPaymentDetailsForAdmin({ request: { offerRequestId } }))
      dispatch(getOfferRequestTripDetailsForAdmin({ request: { offerRequestId } }))
    },
    [demoRouter, dispatch, pageSize, setSelectedUser]
  )

  // Open the trip that carried the items, the way the Trips list does
  const openTrip = useCallback(
    (order: LockerOrderListItem) => {
      if (!order.trip || !order.traveler.hurrierId) return

      const flightItineraryId = order.trip.id.toString()

      setSelectedUser(toUser(order.traveler))
      demoRouter.navigate(`/${adminNavigationPaths.trips}/${order.traveler.hurrierId}/Trips/${flightItineraryId}`)
      dispatch(getTripDetailsForAdmin({ request: { flightItineraryId, page: 1, size: pageSize } }))
      dispatch(getTripOfferRequestsForAdmin({ request: { flightItineraryId, page: 1, size: pageSize } }))
      dispatch(getTripPaymentDetailsForAdmin({ request: { flightItineraryId } }))
    },
    [demoRouter, dispatch, pageSize, setSelectedUser]
  )

  const columns = useMemo(
    () =>
      getLockerOrderTableColumns({
        onOpenOrder: (order) => openOrder(order),
        onOpenItem: (order) => openOrder(order, order.item?.itemRequestId),
        onOpenTrip: openTrip
      }),
    [openOrder, openTrip]
  )

  const isEmpty = !loading && total === 0

  const content = isEmpty ? (
    <Box sx={styles.emptyStateContainer}>
      <EmptyState
        title={statusTab.status ? `No ${statusTab.label} Locker Orders` : 'No Locker Orders Yet'}
        subtitle={
          statusTab.status
            ? 'Locker orders with this status will appear here.'
            : 'Locker orders will appear here once items are dropped off.'
        }
      />
    </Box>
  ) : (
    <Table
      columns={columns}
      data={visibleOrders}
      loading={loading}
      onRowClick={(_, index) => openOrder(visibleOrders[index])}
      rowAttributes={(row) => ({
        'data-locker-order-id': row.externalOrderId,
        'data-order-number': row.orderNumber ?? ''
      })}
      pagination={{
        currentPage: page,
        totalPages,
        totalRecords: total,
        onNext: () => setPage((current) => Math.min(current + 1, totalPages)),
        onPrevious: () => setPage((current) => Math.max(current - 1, 1))
      }}
    />
  )

  return (
    <Box sx={styles.container}>
      <Tabs
        tabs={lockerOrderStatusTabs.map((tab) => ({ label: tab.label, content }))}
        value={statusTabIndex}
        onChange={handleStatusTabChange}
      />
    </Box>
  )
}

export default LockerOrders
